import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { ModalConfirm } from "tamtam-components";
import { toast } from "react-toastify";

import Loader from "../../common/Loader";
import Button from "../../common/Button";
import FormInput from "../../common/FormInput";
import _ from "../../../i18n";
import {
  updateGoPeopleUser,
  updateGoPeopleUserRole,
  saveGoPeopleUserWithRole,
} from "../../../api";
import { LANGUAGES, SELECT_STYLES } from "../../../config";
import {
  validateEmail,
  formatPhone,
  formatHidePhone,
  getRemainAddCollaborator,
  parseApiError,
} from "../../../utils";
import styles from "./Collaborator.module.scss";

// Mirrors the backend rule on User.firstname / User.lastname, declared as
// ^[\p{L}\s'\-]{2,50}$ : letters, spaces, hyphens and apostrophes only.
// Keeping the two in sync is what turns a 400 into an inline field error.
const NAME_REGEX = /^[\p{L}\s'-]{2,50}$/u;

// Mirrors the backend rule on User.phone, declared as ^\+?[0-9\s\-()]{8,25}$ :
// an optional leading +, then 8 to 25 characters among digits, spaces, hyphens
// and parentheses. It counts characters and not digits, which is what lets the
// value produced by formatPhone ("+32 (0)470 12 34 56") through. Replaces the
// Belgian-specific validatePhone here, which prefixes +32 to anything foreign
// and then rejected numbers the backend accepts, such as +212696272161.
const PHONE_REGEX = /^\+?[0-9\s\-()]{8,25}$/;

const rolesOptions = [
  { value: "MANAGER", label: "Admin" },
  { value: "OFFICIAL", label: _("OFFICIAL") },
  { value: "EXTERNAL", label: _("EXTERNAL") },
];

const CollaboratorForm = (props) => {
  const auth = useSelector((state) => state.auth);
  const fiduciary = useSelector(
    (state) => state.auth.navCommunity.selectedOrganization
  );
  const [email, setEmail] = useState("");
  const [lastname, setLastname] = useState("");
  const [firstname, setFirstname] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [roleId, setRoleId] = useState(null);
  const [role, setRole] = useState(rolesOptions[2]);
  const [language, setLanguage] = useState(LANGUAGES[0]);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const [fetchedUser, setFetchedUser] = useState(null);
  const [disableFields, setDisableFields] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [showConfirmMail, setShowConfirmMail] = useState(false);
  const [errors, setErrors] = useState({
    email: "",
    lastname: "",
    firstname: "",
    phone: "",
  });
  const [allowAdd, setAllowAdd] = useState(true);
  // Guards the submit against a burst of clicks. isSaving alone is not enough:
  // several clicks fired before the next render all read the same stale value.
  const isSavingRef = useRef(false);
  const { goPeopleOrganizationId, goPeopleCommunityId } = auth;
  let saveInterval;

  useEffect(() => {
    if (fiduciary && fiduciary.ua_mode === "UNIVERSITY") {
      rolesOptions.forEach((i, idx) => {
        if (i.value === "OFFICIAL") {
          rolesOptions[idx].label = _("student");
        }
      });
    }
  }, [fiduciary]);

  useEffect(() => {
    if (fiduciary && !props.currentItem) {
      const count = getRemainAddCollaborator(
        fiduciary.office_size,
        props.nbCollaborators
      );
      setAllowAdd(count <= 0 ? false : true);
    } else {
      setAllowAdd(true);
    }
  }, [fiduciary, props.currentItem]);

  useEffect(() => {
    if (props.currentItem) {
      setEmail(props.currentItem.email ?? "");
      setFirstname(props.currentItem.firstname ?? "");
      setLastname(props.currentItem.lastname ?? "");
      if (props.currentItem.phone) {
        setPhone(props.currentItem.phone);
        setPhoneValue(props.currentItem.phone);
      }
      setFetchedUser(props.currentItem);

      if (props.currentItem.role) {
        const role = props.currentItem.role;
        if (role) {
          setRoleId(role.uuid);
          //TODO BESOIN DE PLUS D'INFORMATION
          const roleOption = rolesOptions.filter((item) => {
            if (role.role === "LEGAL_REPRESENTATIVE") {
              return ["ADMIN", "MANAGER"].includes(item.value);
            } else {
              return item.value === role.role;
            }
          })[0];
          setRole(roleOption);
        }
      }

      const mainLanguage = props.currentItem.main_language;
      const lang = mainLanguage
        ? LANGUAGES.filter(
            (item) => item.value === mainLanguage.toLowerCase()
          )[0]
        : null;
      setLanguage(lang ?? LANGUAGES[0]);
      setIsValidForm(true);
    }
  }, [props.currentItem]);

  useEffect(() => {
    if (props.resetForm) {
      handleResetForm();
      props.setResetForm(false);
    }
  }, [props.resetForm]);

  useEffect(() => {
    if (email.length > 0 && firstname.length > 0 && lastname.length > 0) {
      setIsValidForm(
        errors.email || errors.lastname || errors.firstname || errors.phone
          ? false
          : true
      );
    }
  }, [errors]);

  // Pure so that validate() can decide on freshly computed values instead of
  // reading the errors state, which is one render behind.
  const getFieldError = (fieldName, fieldValue) => {
    if (fieldValue.length === 0) {
      return _("required_field");
    }
    switch (fieldName) {
      case "email":
        return validateEmail(fieldValue) ? "" : _("validate_email");
      case "lastname":
      case "firstname":
        // Trimmed because the backend trims the name before validating it.
        return NAME_REGEX.test(fieldValue.trim()) ? "" : _("validate_name");
      default:
        return "";
    }
  };

  const getPhoneError = (fieldValue) => {
    if (fieldValue.length === 0) {
      return "";
    }
    // Not trimmed: the backend does not trim the phone, and its character class
    // already allows the surrounding whitespace it accepts.
    return PHONE_REGEX.test(fieldValue) ? "" : _("validate_phone_format");
  };

  const handleKeyUp = (fieldName, fieldValue) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: getFieldError(fieldName, fieldValue),
    }));
  };

  const handleConfirmUserByEmail = () => {
    setFirstname(fetchedUser.firstName ?? "");
    setLastname(fetchedUser.lastName ?? "");
    if (fetchedUser.mainPhone) {
      setPhone(fetchedUser.mainPhone);
      setPhoneValue(formatHidePhone(fetchedUser.mainPhone));
    }
    if (fetchedUser.role) {
      if (fetchedUser.role) {
        const role = rolesOptions.filter((item) => {
          if (fetchedUser.role.role === "LEGAL_REPRESENTATIVE") {
            return (
              item.value ===
              fetchedUser.role.role + "-" + fetchedUser.role.type_status
            );
          } else {
            return item.value === fetchedUser.role.role;
          }
        })[0];
        setRole(role);
      }
    }
    const lang = LANGUAGES.filter(
      (item) => item.value === fetchedUser.language
    )[0];
    setLanguage(lang ?? LANGUAGES[0]);
    setDisableFields(true);
    setShowConfirmMail(false);
    setErrors({
      email: "",
      lastname: "",
      firstname: "",
      phone: "",
    });
  };

  const handlePhoneBlur = (e) => {
    if (phone === phoneValue) {
      setPhone(formatPhone(phone));
      setPhoneValue(formatPhone(phone));
    }
  };

  const handlePhoneKeyUp = (fieldValue) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      phone: getPhoneError(fieldValue),
    }));
  };

  // The backend validates only the fields present in the payload, and handleSave
  // sends a value only when it differs from the loaded one. Checking a field the
  // user never touched would block edits on legacy records, whose names and
  // phones do not always match the current rules, over a value we are not
  // submitting anyway. Same condition as handleSave, so the two stay in step.
  const isFieldSubmitted = (value, storedValue) =>
    !fetchedUser || value !== (storedValue ?? "");

  // Validation errors coming back from the API are attached to their field, and
  // the toast is only used for what could not be tied to one. The canonical field
  // names returned by parseApiError already match this form's error keys.
  const showApiError = (e) => {
    const { fieldErrors, message } = parseApiError(e);

    if (Object.keys(fieldErrors).length > 0) {
      setErrors((prevErrors) => ({ ...prevErrors, ...fieldErrors }));
    }

    toast.error(message);
  };

  const validate = () => {
    const newErrors = {
      email: getFieldError("email", email),
      lastname: isFieldSubmitted(lastname, fetchedUser?.lastname)
        ? getFieldError("lastname", lastname)
        : "",
      firstname: isFieldSubmitted(firstname, fetchedUser?.firstname)
        ? getFieldError("firstname", firstname)
        : "",
      phone: isFieldSubmitted(phone, fetchedUser?.phone)
        ? getPhoneError(phone)
        : "",
    };
    setErrors(newErrors);

    return newErrors.email ||
      newErrors.lastname ||
      newErrors.firstname ||
      newErrors.phone
      ? true
      : false;
  };

  const save = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await handleSave();
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    let error = validate();
    if (error) {
      return null;
    }

    const data = {};
    if (
      (fetchedUser && firstname !== (fetchedUser.firstname ?? "")) ||
      (!fetchedUser && firstname)
    ) {
      data.firstname = firstname;
    }
    if (
      (fetchedUser && lastname !== (fetchedUser.lastname ?? "")) ||
      (!fetchedUser && lastname)
    ) {
      data.lastname = lastname;
    }
    if (
      (fetchedUser && phone !== fetchedUser.phone && phone !== "") ||
      (!fetchedUser && phone)
    ) {
      data.phone = phone;
    }
    if (
      (fetchedUser && language.value !== fetchedUser.main_language) ||
      (!fetchedUser && language)
    ) {
      data.main_language = language.value;
    }
    if (fetchedUser || (!fetchedUser && email)) {
      data.email = email;
    }

    if (
      ((fetchedUser &&
        fetchedUser.role &&
        ((fetchedUser.role.role !== "LEGAL_REPRESENTATIVE" &&
          fetchedUser.role.role === role.value) ||
          (fetchedUser.role.role === "LEGAL_REPRESENTATIVE" &&
            role.value === fetchedUser.role.type_status))) ||
        !fetchedUser) &&
      Object.keys(data).length === 0
    ) {
      return true;
    } else {
      setIsSaving(true);
      if (fetchedUser && fetchedUser.uuid) {
        let promises = [];
        if (
          //TODO A revoir avec amine
          (fetchedUser.role &&
            fetchedUser.role.role !== "LEGAL_REPRESENTATIVE" &&
            fetchedUser.role.role !== role.value) ||
          (fetchedUser.role &&
            fetchedUser.role.role === "LEGAL_REPRESENTATIVE" &&
            role.value !== "MANAGER") ||
          !fetchedUser.role
        ) {
          promises = promises.concat([
            updateGoPeopleUserRole(goPeopleOrganizationId, fetchedUser.uuid, {
              role:
                fetchedUser.role &&
                fetchedUser.role.role === "LEGAL_REPRESENTATIVE" &&
                role.value === "MANAGER"
                  ? "LEGAL_REPRESENTATIVE"
                  : role.value,
              type_status:
                fetchedUser.role &&
                fetchedUser.role.role === "LEGAL_REPRESENTATIVE" &&
                role.value === "MANAGER"
                  ? "ADMIN"
                  : null,
            }),
          ]);
        }
        if (Object.keys(data).length > 0) {
          promises = promises.concat([
            updateGoPeopleUser(fetchedUser.uuid, data),
          ]);
        }
        const response = await Promise.all(promises)
          .then((values) => {
            handleResetForm();
            props.afterSave();
            setIsSaving(false);
            clearInterval(saveInterval);
            toast.success(_("successfully_added"));
            if (props.setSideBar) {
              props.setSideBar("DEFAULT");
            }
          })
          .catch((e) => {
            showApiError(e);
            clearInterval(saveInterval);
            setIsSaving(false);
          });
      } else {
        // Awaited so that isSaving stays true until the request settles and the
        // button keeps showing the loader, like the update branch above.
        await saveGoPeopleUserWithRole(
          goPeopleOrganizationId,
          goPeopleCommunityId,
          {
            ...data,
            role: role.value,
          }
        )
          .then((responseUser) => {
            if (responseUser.exists_in_same_org) {
              toast.info(_("collaborator_exist"));
            } else {
              // Feedback and closing happen as soon as the response arrives.
              // Only the list refresh keeps the delay, because the read side
              // needs the snapshot to catch up with the events just written.
              toast.success(_("successfully_added"));
              if (props.setSideBar) {
                props.setSideBar("DEFAULT");
              }
              setTimeout(() => {
                props.afterSave();
              }, 1000);
            }
            handleResetForm();
            setIsSaving(false);
            clearInterval(saveInterval);
          })
          .catch((e) => {
            setIsSaving(false);
            showApiError(e);
            clearInterval(saveInterval);
          });
      }
    }
  };

  const handleResetForm = (resetEmail = true) => {
    if (resetEmail) {
      setEmail("");
    }
    setFirstname("");
    setLastname("");
    setPhone("");
    setPhoneValue("");
    setDisableFields(false);
    setFetchedUser(null);
    setRoleId(null);
    setRole(rolesOptions[2]);
    setLanguage(LANGUAGES[0]);
    // The required fields are empty again, so the submit must not stay enabled.
    setIsValidForm(false);
  };

  return (
    <div className={props.addFormClass ? styles.form : ""}>
      <div
        className={`${styles.form_content} ${
          props.addFormClass && styles.form_content_pad
        } ${isFetchingUser && styles.loading}`}
      >
        {props.setSideBar && (
          <div
            className={styles.close}
            onClick={() => props.setSideBar("DEFAULT")}
          >
            <i className="icon-ttp-close"></i>
          </div>
        )}
        <div className={styles.titles}>
          {fiduciary.ua_mode === "UNIVERSITY" ? (
            <>
              <h4>
                {props.currentItem
                  ? _("edit_student_title")
                  : _("manual_student_title")}
              </h4>
              <p>
                {props.currentItem
                  ? _("edit_student_subtitle")
                  : _("manual_student_subtitle")}
              </p>
            </>
          ) : (
            <>
              <h4>
                {props.currentItem
                  ? _("edit_collab_title")
                  : _("manual_collab_title")}
              </h4>
              <p>
                {props.currentItem
                  ? _("edit_collab_subtitle")
                  : _("manual_collab_subtitle")}
              </p>
            </>
          )}
        </div>

        {!props.noLimit && !allowAdd ? (
          <div className={styles.upgrade}>
            <h3>
              <i className="icon-ttp-alert-circle"></i> Limite atteinte !
            </h3>
            <p>Vous avez atteint votre limite de nombre de collaborateurs.</p>

            {fiduciary?.uaMode === "CLASSIC" && (
              <>
                <p>
                  Pour pouvoir ajouter de nouveaux collaborateurs vous pouvez
                  augmenter la taille de votre entreprise
                </p>
                {props.setSideBar && (
                  <Button
                    variant="send"
                    onClick={() => props.setShowUpgradeModal(true)}
                  >
                    Ajouter
                  </Button>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <FormInput
              name="email"
              required={true}
              label={_("email_address")}
              autoComplete="none"
              error={errors.email}
              value={email}
              onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
              onChange={(e) => setEmail(e.target.value)}
              disabled={props.currentItem}
            />

            <div className="grid-x grid-margin-x">
              <div className="cell small-12 medium-6">
                <FormInput
                  name="lastname"
                  required={true}
                  label={_("lastname")}
                  autoComplete="none"
                  error={errors.lastname}
                  value={lastname}
                  onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
                  onChange={(e) => setLastname(e.target.value)}
                  disabled={disableFields}
                />
              </div>
              <div className="cell small-12 medium-6">
                <FormInput
                  name="firstname"
                  required={true}
                  label={_("firstname")}
                  autoComplete="none"
                  error={errors.firstname}
                  value={firstname}
                  onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
                  onChange={(e) => setFirstname(e.target.value)}
                  disabled={disableFields}
                />
              </div>
            </div>

            <FormInput
              name="phone"
              label={_("mobile_phone_number")}
              placeholder="+32XXXXXXXX"
              autoComplete="none"
              value={phoneValue}
              error={errors.phone}
              onKeyUp={(e) => handlePhoneKeyUp(e.target.value)}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneValue(e.target.value);
              }}
              handleBlur={(e) => handlePhoneBlur(e)}
              disabled={disableFields && fetchedUser && fetchedUser.mainPhone}
            />

            <div className="grid-x grid-margin-x">
              <div className="cell small-12 medium-6">
                <div className="ttp-form-group">
                  <label className="ttp-label">{_("role")}</label>
                  <Select
                    styles={SELECT_STYLES}
                    options={rolesOptions}
                    isSearchable={false}
                    value={role}
                    onChange={(e) => setRole(e)}
                  />
                </div>
              </div>
              <div className="cell small-12 medium-6">
                <div className="ttp-form-group">
                  <label className="ttp-label">{_("language")}</label>
                  <Select
                    styles={SELECT_STYLES}
                    options={LANGUAGES}
                    isSearchable={false}
                    value={language}
                    onChange={(e) => setLanguage(e)}
                    isDisabled={disableFields}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {(props.noLimit || allowAdd) && (
        <div className={styles.actions}>
          <Button
            variant="default"
            onClick={() => {
              handleResetForm();
              if (props.setSideBar) {
                props.setSideBar("DEFAULT");
              }
            }}
          >
            {_("cancel")}
          </Button>
          {isSaving ? (
            <Button
              variant="primary"
              style={{ paddingTop: "15px", paddingBottom: "15px" }}
            >
              <Loader
                style={{
                  height: "10px",
                }}
                color={"#fff"}
              />
            </Button>
          ) : (
            <Button disabled={!isValidForm} onClick={() => save()}>
              {props.currentItem ? _("edit") : _("add")}
            </Button>
          )}
        </div>
      )}
      <ModalConfirm
        type="publish"
        isOpen={showConfirmMail}
        onCancel={() => {
          handleResetForm();
          setShowConfirmMail(false);
        }}
        onConfirm={() => handleConfirmUserByEmail()}
        title={_("add_confirm")}
        text={_("text_add_exist_collaborator")}
        labelNo={_("no")}
        labelYes={_("yes")}
        labelError={_("error")}
      />
    </div>
  );
};

export default CollaboratorForm;
